/**
 * SearchEventTable Component
 *
 * A TypeScript component that displays events in an AgGrid table with multi-select functionality,
 * allowing users to search, filter, and analyze real ticket events. This component supports
 * multi-row selection, date formatting, and custom cell renderers for event data visualization.
 *
 * @module SearchEvents
 */

import React, { useMemo } from 'react';
import AgGrid from '../../../components/ag-grid/ag-grid';
import { Box, Button, Chip } from '@mui/material';
import { CheckCircleOutline } from '@mui/icons-material';
import dayjs from 'dayjs';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { getScoreColor } from '../AnalysisPage';
import { RawEventData } from '../models/event';
import '../css/style.css';
import { formatDateTime, toLocalTime } from '../../../components/ex_dayjs';
// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

/**
 * Event search parameters interface
 */
interface EventSearch {
    /** Site ID filter */
    siteId?: string;
    /** Event name filter */
    eventName?: string;
    /** Venue name filter */
    venue?: string;
    /** Event type filter */
    eventType?: string;
    /** Category IDs filter */
    categoryIds?: string[];
    /** Category names filter */
    categoryNames?: string[];
    /** Market type filter (e.g., Ticketmaster, AXS) */
    marketType?: string;
    /** Start date for date range filter */
    dateFrom?: Date | string | null;
    /** End date for date range filter */
    dateTo?: Date | string | null;
    /** Recommendation decision filter (Strong Buy, Buy, Hold, Ignore) */
    recommendationDecision?: string;
    /** Simplified score range filter - minimum value */
    simplifiedScoreFrom?: number;
    /** Simplified score range filter - maximum value */
    simplifiedScoreTo?: number;
    /** Score range filter - minimum value */
    scoreFrom?: number;
    /** Score range filter - maximum value */
    scoreTo?: number;
    /** Liquidity consistency score range filter - minimum value */
    liquidityConsistencyScoreFrom?: number;
    /** Liquidity consistency score range filter - maximum value */
    liquidityConsistencyScoreTo?: number;
    /** Available lots filter - when true, filters to events with PrimaryCurrentAvailabilityCount > 0 and ML_Score > 0 */
    availableLots?: boolean;
    /** Exclude purchased seats filter - when true, excludes events with purchased tickets in specific statuses (Sold, Buy, PartiallySold, Active, NonViable) */
    excludePurchased?: boolean;
    /** Exclude TM filter - when true, excludes events where MarketType equals "exact" */
    excludeTM?: boolean;
    /** Percentage of seats remaining filter - filters history events by PrimaryPercentLeft */
    primaryPercentLeft?: number;
}

/**
 * Pre-filtering rule structure
 */
interface PreFilteringRule {
    /** Variable name to filter on */
    variable: string;
    /** Condition type */
    condition: 'less_than_or_equal' | 'greater_than_or_equal' | 'equals' | 'not_in_list' | string;
    /** Value for the condition */
    value: any;
}

/**
 * Filter object with dynamic keys based on OData query conventions
 */
interface FilterObject {
    [key: string]: any;
}

/**
 * Props interface for the SearchEventTable component
 */
interface SearchEventTableProps {
    /** Reference to the AgGrid component for accessing grid API */
    refInput: React.RefObject<any>;
    /** Function to analyze a single event */
    analyzeEvent: (event: any) => void;
    /** Array of event objects to display in the grid */
    searchResults: RawEventData[];
    paginationData: {
        page: number;
        total: number;
        pageSize: number;
        pageCount: number;
    };
    /** Function to update search results */
    updateSearchResult: (result: RawEventData[], total: number, pageIndex: number, pageSizeInput: number) => void;
    /** Filter criteria for the grid */
    filterValue: FilterObject;
    /** Callback function when row selection changes */
    onSelectionChanged?: (selectedRows: RawEventData[]) => void;
    /** Array of row keys that should be selected when the grid mounts or remounts */
    selectedRowKeys?: Array<{ id: any }>;
    /** Resource list name for data fetching (e.g., 'RTP_Event', 'RTP_History') */
    list?: string;
}

/**
 * Return type for fetchPreFilteringRules function
 */
type PreFilteringRulesResponse = PreFilteringRule[];

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * SearchEventTable component for displaying events in an AgGrid with multi-select functionality.
 *
 * @param {SearchEventTableProps} props - Component props
 * @returns {JSX.Element} The SearchEventTable component
 */
const SearchEventTable: React.FC<SearchEventTableProps> = ({
    refInput,
    analyzeEvent,
    searchResults,
    paginationData,
    updateSearchResult,
    filterValue,
    onSelectionChanged,
    selectedRowKeys,
    list = 'RTP_Event' // Default to RTP_Event for backward compatibility
}) => {
    const defaultColDef = useMemo(() => {
        return {
            sortable: true,
            resizable: true,
            flex: 1,
            minWidth: 50,
            filter: false,
            suppressMovable: true,
            singleClickEdit: true,
        };
    }, []);

    /**
     * Handles selection changes in the AgGrid table.
     * Calls the parent component's onSelectionChanged callback with selected rows.
     */
    const handleSelectionChanged = () => {
        if (refInput.current && onSelectionChanged) {
            const selectedRows = refInput.current.getAgGridInstance().api.getSelectedRows();
            onSelectionChanged(selectedRows);
        }
    };

    /**
     * Cell renderer for the Actions column - displays Analyze button
     *
     * @param {ICellRendererParams} params - AgGrid cell renderer parameters
     * @returns {JSX.Element} Button to analyze the event
     */
    const renderActionsButton = (params: ICellRendererParams<RawEventData>): JSX.Element => (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-start', height: '100%' }}>
            <Button size="small" variant="contained" onClick={() => {
                if (params.data) {
                    params.data.isAnalyzed = true;
                    analyzeEvent(params.data);
                }
            }}>{!!!params.data?.recommendationDecision ? 'Analyze' : 'View'}</Button>
        </Box>
    );

    /**
     * Cell renderer for the Analyzed status column - displays chip if analyzed
     *
     * @param {ICellRendererParams} params - AgGrid cell renderer parameters
     * @returns {JSX.Element | null} Chip indicating analyzed status
     */
    const renderAnalyzedChip = (params: ICellRendererParams<RawEventData>): JSX.Element | null => (
        params.data?.isAnalyzed ? (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-start', height: '100%' }}>
                <Chip label="Analyzed" size="small" color="success" icon={<CheckCircleOutline />} />
            </Box>
        ) : null
    );
    const renderDecisionChip = (params: ICellRendererParams<RawEventData>): JSX.Element | null => (
        params.data?.recommendationDecision ? (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-start', height: '100%' }}>
                <Chip label={params.data.recommendationDecision} size="small" color={getScoreColor(params.data.score || 0)} />
            </Box>
        ) : null
    );

    /**
     * Cell renderer for the ML Score column - displays ML score with Buy and StrongBuy counts
     *
     * @param {ICellRendererParams} params - AgGrid cell renderer parameters
     * @returns {JSX.Element | null} Formatted ML score display
     */
    const renderMLScore = (params: ICellRendererParams<RawEventData>): JSX.Element | null => {
        const mlScore = params.data?.mL_Score;
        const mlBuyCount = params.data?.mL_BuyCount;
        const mlStrongBuyCount = params.data?.mL_StrongBuyCount;

        // Build the display text
        const parts: string[] = [];
        const counts: string[] = [];
        if (mlBuyCount && mlBuyCount > 0) {
            counts.push(`B: ${mlBuyCount}`);
        }
        if (mlStrongBuyCount && mlStrongBuyCount > 0) {
            counts.push(`SB: ${mlStrongBuyCount}`);
        }
        
        if (counts.length > 0) {
            parts.push(`(${counts.join(', ')})`);
        }

        const displayText = parts.join(' ');

        return mlScore ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, padding: 0.5, justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Chip label={mlScore} size="small" color={getScoreColor(mlScore)} />
                <span style={{ color: 'gray' }}>{displayText ? displayText : null}</span>
            </Box>
        ) : null;
    };

    /**
     * Cell renderer for the Score column - displays colored chip based on score value
     *
     * @param {ICellRendererParams} params - AgGrid cell renderer parameters
     * @returns {JSX.Element | null} Chip with color based on score range
     */
    const renderScoreChip = (params: ICellRendererParams<RawEventData>): JSX.Element | null => (
        params.data && params.data.score !== undefined && params.data.score >= 0 ? (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-start', height: '100%' }}>
                <Chip
                    label={params.data.score}
                    size="small"
                    color={getScoreColor(params.data.score)}
                />
            </Box>
        ) : null
    );

    /**
     * Cell renderer for the Simplified Score column - displays colored chip based on simplified score value
     *
     * @param {ICellRendererParams} params - AgGrid cell renderer parameters
     * @returns {JSX.Element | null} Chip with color based on score range
     */
    const renderSimplifiedScoreChip = (params: ICellRendererParams<RawEventData>): JSX.Element | null => {
        const simplifiedScore = params.data?.simplifiedScore ?? undefined;
        return simplifiedScore !== undefined && simplifiedScore >= 0 ? (
            <Box sx={{ display: 'flex', gap: 0.25, alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Chip
                    label={simplifiedScore}
                    size="small"
                    color={getScoreColor(simplifiedScore)}
                />
            </Box>
        ) : null;
    };

    const colDefs: ColDef<RawEventData>[] = [
        {
            headerName: 'Event',
            field: 'name',
            minWidth: 220,
            maxWidth: 240,
            checkboxSelection: true,
            headerCheckboxSelection: true,
            cellRenderer: (params: ICellRendererParams<RawEventData>) => {
                let url = params.data?.primary?.url || params.data?.url;
                return (
                    <>
                        {url ? (
                            <a
                                href={url}
                                target='_blank'
                                rel="noreferrer"
                                style={{ textDecoration: 'underline', color: '#58b0f7', fontWeight: '400' }}
                            >
                                {params.value}
                            </a>
                        ) : (
                            <>{params.value}</>
                        )}
                        {params.value && <br />}
                        <span style={{ color: 'gray' }}>SiteID: {params.data?.siteId}</span>
                    </>
                );
            }
        },
        {
            headerName: 'Date',
            field: 'dateUtc',
            maxWidth: 160,
            minWidth: 160,
            cellRenderer: (params: ICellRendererParams<RawEventData>) => {
                let eventDate = params.value ? params.value.replace(/z/i, '') : null;
                return eventDate ? (
                    <>
                        {dayjs(eventDate).format('YYYY-MM-DD')}{' | '}{dayjs(eventDate).format('hh:mm A')}
                        <br />
                        <span style={{ color: 'gray' }}>{`(${dayjs(eventDate).diff(dayjs(), 'day')} days remain)`}</span>
                    </>
                ) : '-';
                
            }
        },
        {
            headerName: 'Venue',
            field: 'venue',
            minWidth: 220,
            maxWidth: 240,
            cellRenderer: (params: ICellRendererParams<RawEventData>) => {
                return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap',  height: '100%', gap: 0.1, overflow: 'hidden' }}>
                        <span style={{ whiteSpace: 'nowrap', display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>{params.value}</span>
                        {params.data?.type && (
                            <Chip label={params.data.type} size="small" color="secondary" sx={{ fontSize: '0.8rem', height: '18px' }} />
                        )}
                    </Box>
                );
            }
        },
        {
            headerName: 'Available',
            field: 'currentAvailableCount',
            minWidth: 70,
            maxWidth: 80,
            type: 'numericColumn',
            valueFormatter: (params: any) => params.data?.primary?.currentAvailableCount
        },
        {
            headerName: 'Venue Capacity',
            headerClass: 'myag-header-cell-wrap-text',
            field: 'oldestAvailableCount',
            minWidth: 100,
            maxWidth: 110,
            type: 'numericColumn',
            valueFormatter: (params: any) => params.data?.primary?.oldestAvailableCount
        },
        {
            headerName: 'OnSale Date',
            field: 'oldestTimestamp',
            maxWidth: 160,
            minWidth: 160,
            cellRenderer: (params: ICellRendererParams<RawEventData>) => {
                const oldestTimestamp = (params.value || params.data?.primary?.oldestTimestamp)?.replace(/z/i, '');
                return oldestTimestamp ? (
                    <>
                        {dayjs(oldestTimestamp).format('YYYY-MM-DD')} {' | '} {dayjs(oldestTimestamp).format('hh:mm A')}
                        <br />
                        <span style={{ color: 'gray' }}>{`(${dayjs().diff(dayjs(oldestTimestamp), 'day')} days passed)`}</span>
                    </>
                ) : '-';
            }
        },
        {
            headerName: 'Category & EventType',
            headerClass: 'myag-header-cell-wrap-text',
            field: 'category_name',
            // sortable: false,
            minWidth: 120,
            maxWidth: 150,
            cellRenderer: (params: ICellRendererParams<RawEventData>) => {
                return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', justifyContent: 'flex-start', height: '100%' }}>
                        {params.value && <Chip label={params.value} color='primary' size='small' sx={{ fontSize: '0.75rem', height: '18px' }}/>}
                        {params.value && <br/>}
                        {params.data?.event_type && <Chip label={params.data.event_type.toLowerCase()} color='success' size='small' sx={{ fontSize: '0.75rem', height: '18px' }}/>}
                    </Box>
                );
            }
        },
        {
            headerName: 'Score',
            field: 'score',
            // sortable: false,
            minWidth: 70,
            maxWidth: 80,
            cellRenderer: renderScoreChip
        },
        {
            headerName: 'Actions',
            field: 'actions',
            sortable: false,
            minWidth: 100,
            maxWidth: 110,
            cellRenderer: renderActionsButton
        },
        {
            headerName: 'ML Score',
            headerClass: 'myag-header-cell-wrap-text',
            field: 'mL_Score',
            minWidth: 60,
            maxWidth: 65,
            cellRenderer: renderMLScore
        },
        // add column "analysisTimestamp"
        {
            headerName: 'Analysis Timestamp',
            headerClass: 'myag-header-cell-wrap-text',
            field: 'analysisTimestamp',
            minWidth: 110,
            maxWidth: 120,
            cellRenderer: (params: ICellRendererParams<RawEventData>) => {
                return params.value ?
                    <><span>{formatDateTime(toLocalTime(params.value), 'MM-DD-YYYY')}</span><br/> <span>{formatDateTime(toLocalTime(params.value), 'hh:mm A')}</span>
                        
                    </> : '';
                    
            }
        }
    ];
   
    if (list === 'RTP_Event') {
        
        const analyzedCol: ColDef<RawEventData> = {
            headerName: 'Is Analyzed',
            field: 'isAnalyzed',
            sortable: false,
            minWidth: 100,
            maxWidth: 110,
            cellRenderer: renderAnalyzedChip
        };
        // Insert the Actions column at index 8 if analyzeEvent function is provided
        colDefs.splice(9, 0, analyzedCol);
    } else {
        const decisionCol: ColDef<RawEventData> = {
            headerName: 'Decision',
            field: 'recommendationDecision',
            minWidth: 80,
            maxWidth: 90,
            cellRenderer: renderDecisionChip
        };
        const simplifiedScoreCol: ColDef<RawEventData> = {
            headerName: 'Simplified Score',
            headerClass: 'myag-header-cell-wrap-text',
            field: 'simplifiedScore',
            minWidth: 80,
            maxWidth: 100,
            cellRenderer: renderSimplifiedScoreChip
        };
        const liquidityConsistencyScoreCol: ColDef<RawEventData> = {
            headerName: 'LCS',
            field: 'liquidity_Consistency_Score',
            minWidth: 75,
            maxWidth: 95,
            cellRenderer: (params: ICellRendererParams<RawEventData>): JSX.Element | null => {
                const lcs = params.data?.liquidity_Consistency_Score;
                return lcs !== undefined && lcs !== null ? (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-start', height: '100%' }}>
                        <Chip label={Number(lcs).toFixed(2)} size="small" color={getScoreColor(lcs)} />
                    </Box>
                ) : null;
            }
        };
        const percentRemainCol: ColDef<RawEventData> = {
            headerName: '% Remain',
            headerClass: 'myag-header-cell-wrap-text',
            field: 'primaryPercentLeft',
            minWidth: 70,
            maxWidth: 85,
            type: 'numericColumn',
            valueFormatter: (params: any) => params.data?.primaryPercentLeft !== undefined && params.data?.primaryPercentLeft !== null
                ? `${Number(params.data.primaryPercentLeft).toFixed(1)}%`
                : '-'
        };
        colDefs.splice(9, 0, decisionCol);
        colDefs.splice(10, 0, simplifiedScoreCol);
        colDefs.splice(11, 0, liquidityConsistencyScoreCol);
        colDefs.splice(13, 0, percentRemainCol);
    }

    const handleOnCellClicked = (ev: any) => {
        // Safely handle environments where `window` may be undefined (SSR) and where getSelection() can return null.
        if (typeof window !== 'undefined') {
            const selection = window.getSelection ? window.getSelection() : null;
            if (selection && selection.toString().length > 0) {
                // If there is a text selection, do not change the row selection.
                return;
            }
        }
        ev.node.setSelected(!ev.node.isSelected());
    };
    return (
        <Box sx={{ width: '98%' }}>
            <AgGrid
                ref={refInput}
                className='analysis-event-table'
                list={list}
                columnDefs={colDefs}
                defaultColDef={defaultColDef}
                rowHeight={60}
                rowSelection='multiple'
                suppressRowClickSelection={true}
                rowMultiSelectWithClick={true}
                rowData={searchResults}
                dataAction={updateSearchResult}
                filterValue={filterValue}
                pagination={paginationData}
                onSelectionChanged={handleSelectionChanged}
                selectedRowKeys={selectedRowKeys}
                onCellClicked={handleOnCellClicked}
            ></AgGrid>
        </Box>
    );
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Converts event search parameters into a filter object.
 *
 * This function transforms user-provided search criteria into a filter object
 * that follows OData query conventions with appropriate suffixes:
 * - `_contains`: For partial string matches
 * - `_eq`: For exact matches
 * - `_ge`: For greater than or equal (dates)
 * - `_le`: For less than or equal (dates)
 * - `_in`: For array/list membership
 *
 * @param {EventSearch} eventSearch - The object containing search criteria
 * @returns {FilterObject} The filter object generated from eventSearch
 */
function buildEventSearchFilter (eventSearch: EventSearch): FilterObject {
    const filterObject: FilterObject = {};
    const {
        siteId,
        eventName,
        venue,
        eventType,
        categoryIds,
        categoryNames,
        marketType,
        dateFrom,
        dateTo,
        recommendationDecision,
        simplifiedScoreFrom,
        simplifiedScoreTo,
        scoreFrom,
        scoreTo,
        liquidityConsistencyScoreFrom,
        liquidityConsistencyScoreTo,
        availableLots,
        excludePurchased,
        excludeTM,
        primaryPercentLeft
    } = eventSearch;

    if (siteId) {
        filterObject.siteId_contains = siteId;
    }

    if (eventName) {
        // string: auto contains
        filterObject.name = eventName;
    }

    if (venue) {
        filterObject.venue = venue;
    }

    if (marketType) {
        filterObject.type_eq = marketType;
    }
    // Using "_eq" for exact matches for the category value.
    if (eventType && !categoryIds) {
        filterObject.event_type_contains = eventType;
    }

    if (categoryIds) {
        filterObject.sm_category_id_in = categoryIds;
    }

    if (categoryNames) {
        filterObject.category_name_in = categoryNames;
    }

    // Using "_ge" (greater than or equal) for the start date.
    if (dateFrom) {
        filterObject.dateUtc_ge = dateFrom;
    }

    // Using "_le" (less than or equal) for the end date.
    if (dateTo) {
        filterObject.dateUtc_le = dateTo;
    }
    if (recommendationDecision) {
        filterObject.recommendationDecision_eq = recommendationDecision;
    }
    if (simplifiedScoreFrom) {
        filterObject.simplifiedScore_ge = simplifiedScoreFrom;
    }
    if (simplifiedScoreTo) {
        filterObject.simplifiedScore_le = simplifiedScoreTo;
    }
    if (scoreFrom) {
        filterObject.score_ge = scoreFrom;
    }
    if (scoreTo) {
        filterObject.score_le = scoreTo;
    }
    if (liquidityConsistencyScoreFrom) {
        filterObject.liquidity_Consistency_Score_ge = liquidityConsistencyScoreFrom;
    }
    if (liquidityConsistencyScoreTo) {
        filterObject.liquidity_Consistency_Score_le = liquidityConsistencyScoreTo;
    }
    if (availableLots !== undefined) {
        filterObject.availableLots = availableLots;
    }
    if (excludePurchased !== undefined) {
        filterObject.excludePurchased = excludePurchased;
    }
    if (excludeTM !== undefined) {
        filterObject.excludeTM = excludeTM;
    }
    if (primaryPercentLeft !== undefined && primaryPercentLeft !== null) {
        filterObject.primaryPercentLeft = primaryPercentLeft;
    }

    return filterObject;
}

/**
 * Builds a filter object from an array of pre-filtering exclusion rules.
 *
 * This function inverts the logic of the exclusion rules to create a filter
 * for records that should be KEPT. It appends suffixes like _le, _ge, _eq, _nin
 * to the variable names to form the filter keys based on the condition type.
 *
 * @param {PreFilteringRule[]} preFilteringRules - The array of rule objects
 * @returns {FilterObject} A filter object with inverted exclusion logic
 */
/**
 * Converts pre-filtering rules into OData filter object for event search queries.
 *
 * Note: The maxPrimaryPrice rule (variable: 'maxPrimaryPrice') is intentionally NOT processed
 * in this function. This rule is used for filtering seat recommendations in AIAnalysisPage.js
 * after scoring, not for filtering events during search. The rule exists in preFilteringRules
 * state for UI configuration purposes but is not applied to event search filters.
 *
 * @param {PreFilteringRule[]} preFilteringRules - Array of pre-filtering rules from configuration
 * @returns {FilterObject} OData filter object for event search
 */
function buildPrefilterObject (preFilteringRules: PreFilteringRule[] = []): FilterObject {
    const filterObject: FilterObject = {};
    if (!preFilteringRules || preFilteringRules.length === 0) {
        return filterObject;
    }

    for (const rule of preFilteringRules) {
        const { variable, condition, value } = rule;

        // Skip rule if essential parts are missing
        if (!variable || !condition || value === undefined) {
            console.warn('Skipping an incomplete rule:', rule);
            continue;
        }

        switch (condition) {
        // Rule: Exclude if value is less than or equal to rule.value.
        // Filter: Keep if value is GREATER THAN rule.value.
            case 'less_than_or_equal':
                if (variable === 'seats_remain_percent' && value) filterObject['seats_remain_percent_le'] = value;
                if (variable === 're_stock_to_date' && value) filterObject['re_stock_to_date_le'] = value;
                // Note: maxPrimaryPrice is intentionally not processed here - see function documentation
                break;

            // Rule: Exclude if value is greater than or equal to rule.value.
            // Filter: Keep if value is LESS THAN rule.value.
            case 'greater_than_or_equal':
                if (variable === 'oldest_available_seats' && value >= 0) filterObject['primary/oldestAvailableCount_ge'] = value;
                if (variable === 're_stock_from_date' && value >= 0) filterObject['re_stock_from_date_ge'] = value;
                break;
            case 'equals':
                if (variable === 'exclude_active_inventory') filterObject['exclude_active_inventory_eq'] = value;
                if (variable === 're_stock') filterObject['re_stock_eq'] = value;
                // History-specific rules: map to existing backend filter keys
                if (variable === 'excludedPurchasedSeats') filterObject['excludePurchased'] = value;
                if (variable === 'hasAvailableLots') filterObject['availableLots'] = value;
                break;

            // Rule: Exclude if value is IN the list.
            // Filter: Keep if value is NOT IN the list.
            case 'not_in_list':
                if (variable === 'eventCategoryName' && value.length) filterObject['category_name_nin'] = value; // "_nin" is a common convention for "not in"
                // History-specific: map excludedMarkets to OData 'marketType' field (not in list)
                if (variable === 'excludedMarkets' && value.length) filterObject['marketType_nin'] = value;
                break;

            default:
                console.warn(`Skipping rule due to unsupported condition: "${condition}"`);
                break;
        }
    }

    return filterObject;
}

/**
 * Builds the final filter object by combining filters from event search and pre-filtering rules.
 *
 * This function merges two filter sources:
 * 1. Event search filters from user input
 * 2. Pre-filtering rules from the scoring configuration
 *
 * @param {EventSearch} eventSearch - The event search object with user criteria
 * @param {PreFilteringRule[]} preFilteringRules - An array of pre-filtering rules
 * @returns {FilterObject} The merged filter object combining both sources
 */
function buildFilterObject (eventSearch: EventSearch = {}, preFilteringRules: PreFilteringRule[] = []): FilterObject {
    // Check if eventSearch is provided; if not, default to an empty object
    eventSearch = eventSearch || {};
    // Check if preFilteringRules is provided; if not, default to an empty array
    preFilteringRules = preFilteringRules || [];

    // Build individual filter objects from event search and pre-filtering rules
    const eventFilter = buildEventSearchFilter(eventSearch);
    const preFilter = buildPrefilterObject(preFilteringRules);

    // TODO: Add OData conversion logic for re-filter configuration rules
    // This placeholder indicates where future OData conversion will be implemented
    // for the re-filter dialog configuration settings

    
    // Merge the two filter objects if use EventSearch
    return {
        ...eventFilter,
        ...preFilter
    };
   
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

const REAL_TICKET_API_URL = process.env.REAL_TICKET_API_URL || 'https://localhost:5002';

/**
 * Fetches pre-filtering rules from the Real Ticket API scoring configuration
 *
 * This async function retrieves the pre-filtering rules from the backend API.
 * These rules are used to filter out events that don't meet certain criteria before scoring.
 * The rules include converted thresholds from the seat-scoring configuration:
 * - Min_Seat_Score_Threshold (from minScoreThreshold)
 * - Min_Event_Score_Threshold (from minEventScoreThreshold)
 * - Top_N_Recommendations (from topNRecommendations)
 * - Max_Primary_Price (new maximum price constraint)
 *
 * @async
 * @returns {Promise<PreFilteringRulesResponse>} Promise that resolves to an array of filtering rules
 * @throws {Error} Throws an error if the API request fails or returns an error response
 *
 * @example
 * const rules = await fetchPreFilteringRules();
 * console.log(rules); // Array of filtering rules from scoring-config.json
 */
export const fetchPreFilteringRules = async (): Promise<PreFilteringRulesResponse> => {
    try {
        const response = await fetch(`${REAL_TICKET_API_URL}/pre-filtering-rules`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return data.preFilteringRules || [];
    } catch (error: any) {
        console.error('Error fetching pre-filtering rules:', error);
        return [];
    }
};

/**
 * Fetches history pre-filtering rules from the Real Ticket API scoring configuration
 *
 * @async
 * @returns {Promise<PreFilteringRulesResponse>} Promise that resolves to an array of history filtering rules
 * @throws {Error} Throws an error if the API request fails or returns an error response
 */
export const fetchHistoryPreFilteringRules = async (): Promise<PreFilteringRulesResponse> => {
    try {
        const response = await fetch(`${REAL_TICKET_API_URL}/history-pre-filtering-rules`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return data.historyPrefilteringRules || [];
    } catch (error: any) {
        console.error('Error fetching history pre-filtering rules:', error);
        return [];
    }
};

// =============================================================================
// EXPORTS
// =============================================================================

export { buildFilterObject, SearchEventTable };
export type {
    EventSearch,
    PreFilteringRule,
    FilterObject,
    SearchEventTableProps,
    PreFilteringRulesResponse
};
